/**
 * Desktop sync bridge.
 *
 * Wires the existing Tauri desktop app to the shared backend for
 * cross-device data synchronisation.  This runs only on the desktop
 * platform and bridges the gap between the local-only persistence
 * (localStorage) and the server-side sync API.
 *
 * Responsibilities:
 * 1. After login: register the device, pull server state.
 * 2. On chart/drawing/setting save: push to server in background.
 * 3. On logout: server already has the latest state.
 *
 * This is the Phase 4 "Desktop Retrofit" from the migration plan.
 */
import { platform, isDesktop } from "@platform";
import { store } from "@store/index";
import { getAccessToken } from "./local-storage-handler";
import syncApi from "@interceptors/sync-instance";
import { pullAllOnLogin } from "./sync-handler";

const API = "/api/v1";

/**
 * Register this desktop device with the server after login.
 * Sends the device ID and a human-readable device name so the user
 * can see/manage logged-in sessions from the web or mobile app.
 */
export async function registerDesktopDevice(): Promise<void> {
  if (!isDesktop || !getAccessToken()) return;

  try {
    const deviceId = await platform.getDeviceId();
    const deviceName = platform.getDeviceName();

    await syncApi.post(`${API}/auth/devices`, {
      device_id: deviceId,
      device_name: deviceName,
      platform: "desktop",
    });
  } catch (e) {
    console.warn("[DesktopSync] Device registration failed:", e);
  }
}

/**
 * Full desktop sync on login.
 *
 * 1. Register the device.
 * 2. Pull all user data from the server (layouts, drawings, settings).
 * 3. Request notification permission (desktop system notifications).
 */
export async function desktopPostLoginSync(): Promise<void> {
  if (!isDesktop) return;

  try {
    await registerDesktopDevice();
    await pullAllOnLogin();
    await platform.requestNotificationPermission();
  } catch (e) {
    console.warn("[DesktopSync] Post-login sync partially failed:", e);
  }
}

/**
 * Update the push token for this desktop device.
 * Desktop doesn't use FCM, so this is a no-op — but the function
 * exists for API symmetry with mobile.
 */
export async function updateDesktopPushToken(): Promise<void> {
  // Desktop uses WebSocket for real-time notifications, not FCM.
  // No push token to register.
}
